/**
 * MobileClientsTab
 *
 * Mobile card-based replacement for the PrimeReact DataTable in ClientsTab.
 * Shows each client as a touch-friendly card with name, V-Fact, managers,
 * created-by info, and a "jump to teams" action.
 * Supports search, pagination, tap-to-edit, and all original modal flows.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Divider,
  Fade,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useDispatch, useSelector } from "react-redux";
import Langs, { Localize } from "../../../app/lang/langs";
import { isAdmin } from "../../../app/utils/helpers";
import { getClients } from "../../../components/clients/clientsSlice";
import MobileAddClientModal from "./MobileAddClientModal";
import MobileEditClientModal from "./MobileEditClientModal";
import MobileInactiveClientsModal from "./MobileInactiveClientsModal";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_SPRING = "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)";
const PAGE_SIZE = 20;

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "14px",
  color: "rgba(0,0,0,0.6)",
  transition: TRANSITION_SMOOTH,
  backdropFilter: "blur(8px)",
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.1)",
    color: "rgb(255,144,21)",
    boxShadow: "0 4px 16px rgba(255,144,21,0.2), 0 1px 4px rgba(255,144,21,0.08)",
    transform: "scale(1.08)",
  },
  "&:active": {
    transform: "scale(0.94)",
    boxShadow: "0 1px 4px rgba(255,144,21,0.08)",
  },
};

const searchFieldSx = {
  mb: 1.5,
  "& input": { fontSize: "16px" },
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    bgcolor: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(16px) saturate(1.3)",
    WebkitBackdropFilter: "blur(16px) saturate(1.3)",
    transition: TRANSITION_SMOOTH,
    boxShadow: "0 2px 8px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.8)",
    "& fieldset": {
      borderColor: "rgba(255,144,21,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,144,21,0.2)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12), 0 4px 16px rgba(255,144,21,0.08)",
    },
    "&.Mui-focused": {
      boxShadow: "0 4px 20px rgba(255,144,21,0.1), inset 0 1px 0 rgba(255,255,255,0.9)",
    },
  },
};

export default function MobileClientsTab() {
  const mounted = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clients = useSelector((state) => state.clients.list);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const userRoleId = useSelector((state) => state.login.userRoleId);

  const [clientsList, setClientsList] = useState([]);
  const [addClientModalVisibility, setAddClientModalVisibility] = useState(false);
  const [onEditClient, setOnEditClient] = useState(null);
  const [editClientModalVisibility, setEditClientModalVisibility] = useState(false);
  const [inactiveModalVisibility, setInactiveModalVisibility] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [expandedManagersId, setExpandedManagersId] = useState(null);

  useEffect(() => {
    if (!mounted.current) {
      dispatch(getClients());
    }
    mounted.current = true;
  }, [mounted]);

  useEffect(() => {
    setClientsList(clients);
  }, [clients]);

  /* ---- Filtering ---- */
  const filteredData = useMemo(() => {
    let list = clientsList || [];
    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      list = list.filter((client) => {
        const searchStr = [client.nom, client.vfact, client.createdBy]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }
    return list;
  }, [clientsList, searchText]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ---- Handlers ---- */
  const handleCardTap = (client) => {
    setOnEditClient(client);
    setEditClientModalVisibility(true);
  };

  const handleNavToTeam = (e, client) => {
    e.stopPropagation();
    const encodedLabel = encodeURIComponent(client.label);
    navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
  };

  const toggleManagers = (e, clientId) => {
    e.stopPropagation();
    setExpandedManagersId((prev) => (prev === clientId ? null : clientId));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return moment(timestamp).format("DD/MM/YYYY HH:mm");
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        // Clears the home indicator on notched phones.
        pb: "calc(20px + env(safe-area-inset-bottom))",
        mt: 0,
        // Fills the flex parent in MobileClientsView. The old calc(100vh - 130px)
        // both guessed the header height and used vh, which overshoots the visual
        // viewport on iOS Safari and hid the pagination row below the fold.
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar + action buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={Localize("Search") + "..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "rgba(255,144,21,0.45)" }} />
              </InputAdornment>
            ),
          }}
          sx={searchFieldSx}
        />
        {isAdmin(userRoleId) && (
          <Tooltip title={<Langs str="Archive" />}>
            <IconButton
              onClick={() => setInactiveModalVisibility(true)}
              sx={{ ...iconButtonSx, flexShrink: 0 }}
            >
              <ArchiveOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={<Langs str="ADD_CLIENT" />}>
          <IconButton
            onClick={() => setAddClientModalVisibility(true)}
            sx={{
              ...iconButtonSx,
              flexShrink: 0,
              color: "rgb(255,144,21)",
              background: "linear-gradient(135deg, rgba(255,144,21,0.08) 0%, rgba(255,193,0,0.06) 100%)",
              "&:hover": {
                ...iconButtonSx["&:hover"],
                color: "rgb(255,144,21)",
                background: "linear-gradient(135deg, rgba(255,144,21,0.15) 0%, rgba(255,193,0,0.1) 100%)",
              },
            }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 600,
          letterSpacing: "0.03em",
          opacity: 0.55,
          fontSize: "0.72rem",
          textTransform: "uppercase",
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,144,21,0.15)",
            borderRadius: 3,
            "&:hover": { background: "rgba(255,144,21,0.25)" },
          },
        }}
      >
        {paginatedData.map((client, index) => (
          <Fade in key={client.id || index} timeout={250 + index * 50}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "18px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.88)",
                backdropFilter: "blur(20px) saturate(1.3)",
                WebkitBackdropFilter: "blur(20px) saturate(1.3)",
                border: "1px solid rgba(255,144,21,0.06)",
                boxShadow:
                  "0 4px 16px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(255,255,255,0.5) inset",
                transition: TRANSITION_SMOOTH,
                "&:active": {
                  transform: "scale(0.975)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardActionArea onClick={() => handleCardTap(client)} sx={{ p: 0 }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Top row: client name + V-Fact */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {/* Avatar circle */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "14px",
                          background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "0.88rem",
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(255,144,21,0.25), 0 1px 3px rgba(255,144,21,0.15)",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {(client.nom?.[0] || "C").toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.92rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.015em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "rgba(0,0,0,0.85)",
                          }}
                        >
                          {client.nom}
                        </Typography>
                        {client.vfact && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.73rem",
                              opacity: 0.6,
                              color: "rgba(255,144,21,0.8)",
                              fontWeight: 500,
                            }}
                          >
                            V-Fact: {client.vfact}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Managers section */}
                  {client.managers && client.managers.length > 0 && (
                    <Box sx={{ mt: 0.75 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <PersonOutlineRoundedIcon
                          sx={{ fontSize: "0.88rem", color: "rgba(255,144,21,0.5)" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            color: "rgba(0,0,0,0.4)",
                          }}
                        >
                          <Langs str="RESP_COMPTE" />
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 0.25 }}>
                        {client.managers
                          .slice(0, expandedManagersId === client.id ? client.managers.length : 3)
                          .map((manager, mIdx) => (
                            <Chip
                              key={mIdx}
                              label={`${manager.prenom} ${manager.nom}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 26,
                                borderRadius: "10px",
                                transition: TRANSITION_SMOOTH,
                                borderColor: "rgba(0,0,0,0.08)",
                                bgcolor: "rgba(255,255,255,0.6)",
                                backdropFilter: "blur(8px)",
                                fontWeight: 500,
                              }}
                            />
                          ))}
                        {client.managers.length > 3 && (
                          <Chip
                            label={
                              expandedManagersId === client.id
                                ? Localize("SHOW_LESS")
                                : `+${client.managers.length - 3} ${Localize("MORE")}`
                            }
                            size="small"
                            onClick={(e) => toggleManagers(e, client.id)}
                            sx={{
                              fontSize: "0.7rem",
                              height: 26,
                              borderRadius: "10px",
                              fontWeight: 700,
                              color: "rgb(255,144,21)",
                              border: "1.5px solid rgba(255,144,21,0.35)",
                              bgcolor: "rgba(255,144,21,0.04)",
                              cursor: "pointer",
                              transition: TRANSITION_SPRING,
                              "&:hover": {
                                bgcolor: "rgba(255,144,21,0.1)",
                                borderColor: "rgba(255,144,21,0.5)",
                              },
                              "&:active": {
                                transform: "scale(0.93)",
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Meta row: created by + date */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1.5,
                      mt: 1,
                      pt: 0.75,
                      borderTop: "1px solid rgba(0,0,0,0.04)",
                    }}
                  >
                    {client.createdBy && (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem", opacity: 0.45, fontWeight: 500 }}
                      >
                        <Langs str="created_by" />: {client.createdBy}
                      </Typography>
                    )}
                    {client.createdAt && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <CalendarTodayOutlinedIcon
                          sx={{ fontSize: "0.75rem", opacity: 0.35 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem", opacity: 0.45, fontWeight: 500 }}
                        >
                          {formatDate(client.createdAt)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>

              {/* Action buttons row */}
              <Divider sx={{ opacity: 0.3, borderColor: "rgba(255,144,21,0.08)" }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  px: 1,
                  py: 0.25,
                }}
              >
                <Tooltip title={<Langs str="JUMP_TO_TEAMS" />}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleNavToTeam(e, client)}
                    sx={{
                      minWidth: 44,
                      minHeight: 44,
                      borderRadius: "12px",
                      transition: TRANSITION_SPRING,
                      color: "rgba(255,144,21,0.6)",
                      "&:hover": {
                        backgroundColor: "rgba(255,144,21,0.08)",
                        color: "rgb(255,144,21)",
                        transform: "translateX(3px) scale(1.08)",
                      },
                      "&:active": { transform: "scale(0.93)" },
                    }}
                  >
                    <ArrowForwardIosRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography
                variant="body2"
                sx={{ opacity: 0.35, fontWeight: 500, letterSpacing: "-0.01em" }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                minWidth: 40,
                height: 40,
                margin: "0 3px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}

      {/* Modals - same as desktop */}
      <MobileAddClientModal
        visible={addClientModalVisibility}
        setVisible={setAddClientModalVisibility}
      />
      <MobileEditClientModal
        visible={editClientModalVisibility}
        setVisible={setEditClientModalVisibility}
        client={onEditClient}
        setClient={setOnEditClient}
      />
      <MobileInactiveClientsModal
        visible={inactiveModalVisibility}
        setVisible={setInactiveModalVisibility}
      />
    </Box>
  );
}
